import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ArrowLeft, CircleHelp, ExternalLink, Loader2, Sparkles } from "lucide-react";
import { ArticleCard, ArticleView, EJEMPLOS, SearchBox, SearchResults, useHelpSearch } from "@/components/help/HelpUI";

/** Botón «?» + panel lateral de ayuda contextual: no sale de la pantalla actual. */
export function HelpButton({ className = "", label = true }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const ruta = loc.pathname;
  const [ctx, setCtx] = useState(null);
  const [art, setArt] = useState(null);
  const s = useHelpSearch(ruta);

  useEffect(() => {
    if (!open) return;
    setArt(null); s.setRes(null); s.setQ("");
    api.get("/help/home", { params: { ruta } }).then((r) => setCtx(r.data)).catch(() => setCtx({ contexto: [], populares: [] }));
  }, [open, ruta]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrir = async (slug) => {
    setArt({ cargando: true });
    try { const { data } = await api.get(`/help/articles/${slug}`); setArt(data); } catch (_) { setArt({ error: true }); }
  };
  const sugeridos = ctx?.contexto?.length ? ctx.contexto : ctx?.populares || [];

  return (
    <>
      <button data-testid="help-open-btn" onClick={() => setOpen(true)} title="Centro de Ayuda"
        className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-xl border border-sky-200 bg-sky-50 text-sky-700 hover:bg-sky-100 text-sm font-semibold transition-colors shrink-0 ${className}`}>
        <CircleHelp className="w-4 h-4" />{label && <span className="hidden lg:inline">Ayuda</span>}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" data-testid="help-panel" className="w-full sm:max-w-xl overflow-y-auto bg-[#f6fafd] p-0 [&>button]:z-20 [&>button]:text-white [&>button]:opacity-90">
          <SheetHeader className="sticky top-0 z-10 bg-[#0b1b33] text-white px-5 py-4 space-y-3">
            <div className="flex items-center justify-between gap-3 pr-8">
              <SheetTitle className="text-white font-display text-lg flex items-center gap-2"><CircleHelp className="w-5 h-5 text-sky-300" /> Centro de Ayuda</SheetTitle>
              <a href={`/admin/ayuda?desde=${encodeURIComponent(ruta)}`} target="_blank" rel="noreferrer" data-testid="help-panel-fullpage"
                className="text-xs font-semibold text-sky-200 hover:text-white flex items-center gap-1">Abrir completo <ExternalLink className="w-3.5 h-3.5" /></a>
            </div>
            <SearchBox s={{ ...s, buscar: (t) => { setArt(null); s.buscar(t); } }} autoFocus />
          </SheetHeader>
          <div className="p-5 space-y-5">
            {art ? (
              <div className="space-y-4">
                <button data-testid="help-panel-back" onClick={() => setArt(null)} className="text-sm font-semibold text-sky-700 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Volver</button>
                {art.cargando && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
                {art.error && <p className="text-sm text-slate-600">No se pudo abrir el artículo.</p>}
                {art.slug && <ArticleView a={art} onOpen={abrir} nuevaPestana />}
              </div>
            ) : s.res ? (
              <SearchResults s={s} onOpen={abrir} />
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {EJEMPLOS.slice(0, 4).map((e) => (
                    <button key={e} onClick={() => s.buscar(e)} className="text-xs rounded-full bg-white border border-slate-200 hover:border-sky-300 px-3 py-1.5 text-slate-600">{e}</button>
                  ))}
                </div>
                <div className="space-y-2" data-testid="help-panel-contexto">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-sky-500" /> {ctx?.contexto?.length ? "Ayuda para esta pantalla" : "Artículos populares"}
                  </p>
                  {!ctx && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
                  {sugeridos.map((a) => <ArticleCard key={a.slug} a={a} onOpen={abrir} compact />)}
                  {ctx && !sugeridos.length && <p className="text-sm text-slate-500">Aún no hay artículos publicados.</p>}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
